import AbstractAnalyticProvider from '../AbstractAnalyticProvider';
import EventModelFactory from './EventModelFactory';
import {createHTMLElement, isServer} from 'coreUtils/utils';
import {
	EGoogleTagManagerEventType,
	IDataLayerEvent,
	IGoogleTagManagerConfiguration,
	IPersistentEvent,
	IPersistentEvents
} from './IGoogleTagManager';
import EventModel from './eventModels/EventModel';
import {EEventSource, EEventType, IEventData} from 'coreClasses/events/IEvents';
import ECommerceProductClickEventModel from './eventModels/ecommerce/EcommerceProductClickEventModel';
import PageViewEventModel from './eventModels/common/PageViewEventModel';
import UserInfoEventModel from './eventModels/common/UserInfoEventModel';
import ManagerRepository from 'coreClasses/ManagerRepository';
import {GTMEVENT_BANNED_KEYS} from 'brandConstants';

const DEFAULT_GTM_BASE_URL = 'https://www.googletagmanager.com';

/**
 * Represents GoogleTagManager
 */
class GoogleTagManager extends AbstractAnalyticProvider {
	protected configuration: IGoogleTagManagerConfiguration;
	protected dataLayer: IDataLayerEvent[] = [];
	protected eventModelFactory: EventModelFactory;
	protected persistentEvents: IPersistentEvents;
	protected onPageReadyEvents: IDataLayerEvent[];
	protected managerRepository: ManagerRepository;
	protected isPageReady: boolean;

	/**
	 * @constructor
	 * @param {IGoogleTagManagerConfiguration} configuration - configuration
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 */
	constructor(managerRepository: ManagerRepository, configuration: IGoogleTagManagerConfiguration) {
		super();

		this.configuration = configuration;
		this.eventModelFactory = new EventModelFactory(managerRepository);
		this.persistentEvents = {};
		this.isPageReady = false;
		this.onPageReadyEvents = [];
		this.managerRepository = managerRepository;

		if (!isServer) {
			window.dataLayer = window.dataLayer || [];
			this.dataLayer = window.dataLayer;
		}
	}

	/**
	 * Tracks analytics event
	 * @param {EEventType} eventType - event type
	 * @param {IEventData} eventData - event data
	 */
	update(eventType: EEventType, eventData: IEventData): void {
		const gtmEventType = EGoogleTagManagerEventType[EEventType[eventType]];
		const gtmEventData: IEventData = {
			...eventData,
			event: gtmEventType,
			configuration: this.configuration
		};

		if ([
			EGoogleTagManagerEventType.PRODUCT_DETAIL_VIEW,
			EGoogleTagManagerEventType.PRODUCT_ADD_TO_CART
		].indexOf(gtmEventType) !== -1) {
			const savedProductClickEvent: IPersistentEvent
				= this.getSavedPersistentEvent(EGoogleTagManagerEventType.PRODUCT_CLICK);

			if (savedProductClickEvent && gtmEventData?.source !== EEventSource.VIRTUAL_TRY_ON) {
				const productClickEvent = savedProductClickEvent.event as ECommerceProductClickEventModel;

				gtmEventData.sourceSpecification = productClickEvent.getSource();
			}
		}

		const gtmEvent: EventModel = this.eventModelFactory.create(gtmEventType, gtmEventData);

		if (gtmEvent === null) {
			return;
		}

		if (gtmEvent.isPersistentEvent()) {
			const savedEvent: IPersistentEvent = this.getSavedPersistentEvent(
				gtmEvent.event as EGoogleTagManagerEventType,
			);

			if (savedEvent) {
				if (gtmEvent.isEventShouldBeKept(savedEvent.event)) {
					this.replaceEventInDataLayer(gtmEvent, savedEvent.position);

					if (gtmEvent.event === EGoogleTagManagerEventType.CUSTOMER_CHANGED) {
						const savedPageViewEvent = this.getSavedPersistentEvent(EGoogleTagManagerEventType.PAGE_VIEW);
						const pageViewEvent = savedPageViewEvent.event as PageViewEventModel;

						pageViewEvent.userInfo = gtmEvent as any;
					}

					return;
				}
			}
		}

		this.pushEventToDataLayer(gtmEvent);
	}

	/**
	 * Push event to Data Layer
	 * @param {EventModel} gtmEvent - event
	 */
	protected pushEventToDataLayer(gtmEvent: EventModel): void {
		GTMEVENT_BANNED_KEYS?.forEach(key => delete gtmEvent[key]);

		if (this.isPageReady || gtmEvent.isIndependentEvent()) {
			this.dataLayer.push(gtmEvent);

			if (gtmEvent.isPersistentEvent()) {
				this.savePersistentEvent(gtmEvent);
			}
		} else if (gtmEvent.event === EGoogleTagManagerEventType.PAGE_VIEW) {
			this.processInitialPageViewEvent(gtmEvent as PageViewEventModel);
		} else {
			this.onPageReadyEvents.push(gtmEvent);
		}
	}

	/**
	 * Processes initial page view event
	 * @param {PageViewEventModel} gtmEvent - event
	 */
	protected processInitialPageViewEvent(gtmEvent: PageViewEventModel) {
		this.isPageReady = true;
		this.pushEventToDataLayer(gtmEvent);

		if (this.onPageReadyEvents.length) {
			const customerChangedEventIndex: UserInfoEventModel
					= this.onPageReadyEvents.findIndex((onPageReadyEvent: EventModel) =>
						onPageReadyEvent.event === EGoogleTagManagerEventType.CUSTOMER_CHANGED) as any;

			if (customerChangedEventIndex !== undefined) {
				this.pushEventToDataLayer(this.onPageReadyEvents[customerChangedEventIndex as any] as any);
				this.onPageReadyEvents.splice(customerChangedEventIndex as any, 1);
			}

			this.onPageReadyEvents.forEach((onPageReadyEvent)=> {
				this.pushEventToDataLayer(onPageReadyEvent as any);
			});
			this.onPageReadyEvents = [];
		}
	}

	/**
	 * Push event to Data Layer
	 * @param {EventModel} gtmEvent - event
	 * @param {Number} position - position
	 */
	protected replaceEventInDataLayer(gtmEvent: EventModel, position: number): void {
		GTMEVENT_BANNED_KEYS?.forEach(key => delete gtmEvent[key]);

		this.dataLayer[position] = gtmEvent;

		if (gtmEvent.isPersistentEvent()) {
			this.savePersistentEvent(gtmEvent, position);
		}
	}

	/**
	 * Saves event to the separate storage
	 * @param {EventModel} gtmEvent - event
	 * @param {number} position - position
	 */
	protected savePersistentEvent(gtmEvent: EventModel, position: number = this.getLastDataLayerPosition()) {
		this.persistentEvents[gtmEvent.event] = {position, event: gtmEvent};
	}

	/**
	 * Returns last data layer position
	 * @returns {number} - last data layer position
	 */
	protected getLastDataLayerPosition(): number {
		return this.dataLayer.length - 1;
	}

	/**
	 * Returns saved event by eventType
	 * @param {EGoogleTagManagerEventType} eventType - event type
	 * @returns {IPersistentEvent} - saved event
	 */
	protected getSavedPersistentEvent(eventType: EGoogleTagManagerEventType): IPersistentEvent {
		return this.persistentEvents[eventType];
	}

	/**
	 * Load additional arguments to data layer
	 */
	protected loadAdditionalArguments(){
		this.dataLayer.push(arguments);
	}

	/**
	 * Load integration scripts.
	 * Supports per-container base URL overrides via configuration.containerBaseUrls.
	 */
	public loadIntegrationScripts(): void {
		if (!isServer) {
			const {containerBaseUrls} = this.configuration;

			for (let i = 0, len = this.configuration.containerId.length; i < len; i++) {
				const containerId = this.configuration.containerId[i];
				const customBaseUrl = containerBaseUrls?.[containerId];
				const baseUrl = customBaseUrl || DEFAULT_GTM_BASE_URL;

				document.head.insertBefore(
					createHTMLElement(
						'script',
						{
							id: 'gtmScript',
							innerHTML: this.getIntegrationHeadScript(containerId, baseUrl),
						}),
					document.head.childNodes[0]
				);

				document.body.insertBefore(
					createHTMLElement('noscript', {innerHTML: this.getIntegrationBodyScript(containerId, baseUrl)}),
					document.body.childNodes[0]
				);
			}
		}
	}

	/**
	 * Returns the integration head script
	 * @param {String} containerId - container Id
	 * @param {String} baseUrl - base URL
	 * @returns {String} - integration head script
	 */
	 private getIntegrationHeadScript(containerId: string, baseUrl: string = DEFAULT_GTM_BASE_URL): string {
		return `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
			new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
			j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
			'${baseUrl}/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
			})(window,document,'script','dataLayer','${containerId}');`;
	}

	/**
	 * Returns the integration body script
	 * @param {String} containerId - container Id
	 * @param {String} baseUrl - base URL
	 * @returns {String} - integration body script
	 */
	private getIntegrationBodyScript(containerId: string, baseUrl: string = DEFAULT_GTM_BASE_URL): string {
		return `<iframe src="${baseUrl}/ns.html?id=${containerId}"
			height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
	}
}

export default GoogleTagManager;
